import { createContext, useContext, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useRemote, formatDate, useStorage } from '../../../Utils/Utils';
import { PAGES, ROUTE_PATHS, BannerContext } from '../../../InternalApiApp';
import { Route, Switch } from 'react-router-dom/cjs/react-router-dom.min';
import Page, { PageContentBodyGrid } from '../../Shared/Page/Page';
import PageGridItem from '../../Shared/Page/PageGridItem';
import PageGridDivider from '../../Shared/Page/PageGridDivider';
import ActionButtonsBanner from '../../Shared/Banner/ActionButtonsBanner';
import PageContentToggle from '../../../components/Menus/PageContentToggle';
import OfferFootnotes from './OfferTabs/OfferFootnotes';
import OfferGeneral from './OfferTabs/OfferGeneral';
import OfferDisclaimers from './OfferTabs/OfferDisclaimers';
import "./FundPage.scss"

export const OfferContext = createContext();


const OfferPage = ({ portfolio }) => {
    const remote = useRemote();
    const { offerId } = useParams()
    const history = useHistory()
    const { setBannerBreadCrumbs } = useContext(BannerContext);

    const [originalOffer, setOriginalOffer] = useState(null)
    const [offer, setOffer] = useState(null)
    const [editMode, setEditMode] = useState(false)
    const [autoFocus, setAutoFocus] = useState(false)
    const [notes, setNotes] = useState([])
    // const [managers, setManagers] = useState([])
    const [selectedTab, setSelectedTab] = useState('General')
    const [groupings, setGroupings] = useState([]);
    const [hierarchy, setHierarchy] = useState([]);
    const [disclaimers, setDisclaimers] = useState([]);
    const [originalDisclaimers, setOriginalDisclaimers] = useState([])
    const [disclaimersSelected, setDisclaimersSelected] = useState([]);

    const storage = useStorage();

    const linkStem = portfolio ? `${ROUTE_PATHS[PAGES.ADMIN]}/portfolio-management/products/${offerId}` : `${ROUTE_PATHS[PAGES.ADMIN]}/fund-management/offers/${offerId}`


    useEffect(() => {
        remote.get(`/funds/offers/${offerId}`).then(fetchOffer => {
            setOriginalOffer(fetchOffer)
            setOffer(fetchOffer)
            setBannerBreadCrumbs({ tab: portfolio ? 'Products' : "Offers", param1: { name: fetchOffer?.offerName, id: offerId } })
            setNotes(fetchOffer.disclosures)

            remote.get(`/disclaimers/offer/${fetchOffer.id}`).then(fetchedDisclaimers => {
                const selected = fetchedDisclaimers.filter(d => d.selected).map(d => d.id)
                const sortedDisclaimers = fetchedDisclaimers.sort((a, b) => a.name.localeCompare(b.name));
                setDisclaimers(sortedDisclaimers);
                setOriginalDisclaimers(sortedDisclaimers);
                setDisclaimersSelected(selected)
            })
            if (portfolio) {
                remote.get(`funds/funds/scheme/${fetchOffer.schemeNumber}/groupings`).then(setGroupings);
            };
        });


    }, [remote, offerId])

    useEffect(() => {
        storage.getOrFetch("funds/portfolio/groupings/hierarchy").then((hierarchy) => {
            hierarchy = hierarchy.filter((item) => item.displaysInHierarchy).sort((a, b) => a.hierarchyOrder - b.hierarchyOrder).map((item) => item.groupingName);
            setHierarchy(hierarchy);
        })
    }, []);

    function saveEdits() {
        remote.post(`/funds/offers/${offerId}`, offer).then(updatedOffer => {
            setOriginalOffer(updatedOffer)
            setOffer(updatedOffer)
            setNotes(updatedOffer.disclosures)

            const disclaimersIdsList = disclaimers.filter(d => d.selected).map(d => d.id).toString()
            if (disclaimersSelected.toString() !== disclaimersIdsList) {
                remote.put(`/disclaimers/offer/${offer.id}`, disclaimers).then(resp => {
                    const selected = resp && resp.length > 0 ? resp.filter(d => d.selected).map(d => d.id) : [];
                    const sortedResp = resp.sort((a, b) => a.name.localeCompare(b.name));
                    setDisclaimers(sortedResp);
                    setOriginalDisclaimers(sortedResp);
                    setDisclaimersSelected(selected)
                }).catch(() => {
                    setDisclaimers(originalDisclaimers)
                })
            }
        })
        setEditMode(false)
        setAutoFocus(false)
    }

    function cancelEdits() {
        setOffer(originalOffer)
        setNotes(originalOffer.disclosures)
        setDisclaimers(originalDisclaimers)
        setEditMode(false)
        setAutoFocus(false)
    }

    function changeTab(string) {
        if (string === "General") {
            return history.push(linkStem + ``)
        } else if (string === "Footnotes") {
            return history.push(linkStem + `/footnotes`)
        } else if (string === "Note Disclaimers") {
            return history.push(linkStem + `/disclaimers`)
        }
    }

    const labels = [
        { label: "General", active: selectedTab === "General", onClick: () => changeTab("General") },
        { label: "Footnotes", active: selectedTab === "Footnotes", onClick: () => changeTab("Footnotes") },
        { label: "Note Disclaimers", active: selectedTab === "Note Disclaimers", onClick: () => changeTab("Note Disclaimers") },
    ]

    return (
        <Page noBanner >
            <PageContentBodyGrid gridColCount="12" subGrid paddingBottom="2.5rem">
                <PageGridItem gap='l' direction='col' col="1 / span 12" >
                    <ActionButtonsBanner
                        className="pt-l"
                        hideBackButton
                        editMode={editMode}
                        edit={{ onClick: () => setEditMode(true) }}
                        duplicate={{ disabled: true }}
                        remove={{ disabled: true }}
                        cancel={{ onClick: () => { cancelEdits() } }}
                        save={{ onClick: () => { saveEdits() } }}
                    />
                    <PageGridDivider />
                </PageGridItem>
                <PageGridItem gap='m' direction='col' col="1 / span 12" >
                    <PageContentToggle style={{ overflowX: 'auto', scrollbarWidth: 'none' }} labels={labels} />
                    <PageGridDivider />
                </PageGridItem>
                <PageGridItem direction='col' col="1 / span 12" style={{ overflowY: 'auto', height: "calc(100vh - 20.75rem)" }}>
                    <PageContentBodyGrid subGrid>
                        <OfferContext.Provider
                            value={{
                                offer: offer,
                                setOffer: (val) => setOffer(val),
                                originalOffer: originalOffer,
                                setOriginalOffer: (val) => setOriginalOffer(val),
                                editMode: editMode,
                                setEditMode: (boolean) => setEditMode(boolean),
                                selectedTab: selectedTab,
                                setSelectedTab: (val) => setSelectedTab(val),
                                notes: notes,
                                setNotes: (val) => setNotes(val),
                                autoFocus: autoFocus,
                                setAutoFocus: (val) => setAutoFocus(val),
                                groupings: groupings,
                                disclaimers: disclaimers,
                                setDisclaimers: (val) => setDisclaimers(val),
                                disclaimersSelected: disclaimersSelected,
                                setDisclaimersSelected: (val) => setDisclaimersSelected(val)
                            }}
                        >
                            <Switch >
                                <Route exact path={ROUTE_PATHS[PAGES.ADMIN] + `/fund-management/offers/:offerId`} render={() => <OfferGeneral />} />
                                <Route exact path={ROUTE_PATHS[PAGES.ADMIN] + `/fund-management/offers/:offerId/footnotes`} render={() => <OfferFootnotes />} />

                                <Route exact path={ROUTE_PATHS[PAGES.ADMIN] + `/portfolio-management/products/:offerId`} render={() => <OfferGeneral portfolio name="Portfolio" />} />
                                <Route exact path={ROUTE_PATHS[PAGES.ADMIN] + `/portfolio-management/products/:offerId/footnotes`} render={() => <OfferFootnotes portfolio />} />
                                <Route exact path={ROUTE_PATHS[PAGES.ADMIN] + `/portfolio-management/products/:offerId/disclaimers`} render={() => <OfferDisclaimers portfolio />} />
                            </Switch>
                        </OfferContext.Provider>
                    </PageContentBodyGrid>
                </PageGridItem>

                {/* <PageGridItem container col="1 / span 6" direction='col' gap="xl" style={{ display: 'flex', paddingRight: '1.5rem' }}>
                    <PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid>
                        <PageGridItem container col="1 / span 6" style={{ marginTop: '1.5rem' }}>
                            <DetailSectionHeader header={portfolio ? "Product Details" : "Offer Details"} icon={<FileText />} />
                        </PageGridItem>
                        <PageGridItem col="1 / span 3">
                            <DetailField
                                bold
                                editMode={editMode}
                                onChange={(e) => updateOffer(e.target.value, "offerName")}
                                label={portfolio ? "Product Name" : "Offer Name"}
                                value={offer?.offerName}
                            />
                        </PageGridItem>
                        <PageGridItem col="4 / span 3">
                            <DetailField
                                bold
                                editMode={editMode}
                                onChange={(e) => updateOffer(e.target.value, "offerNumber")}
                                label={portfolio ? "Product Number" : "Offer Number"}
                                value={offer?.offerNumber}
                            />
                        </PageGridItem>
                        <PageGridItem col="1 / span 3">
                            <DetailField
                                bold
                                editMode={editMode}
                                onChange={(e) => updateOffer(e.target.value, "status")}
                                label="Status"
                                value={offer?.status}
                            />
                        </PageGridItem>
                        <PageGridItem col="1 / span 3">
                            <DetailField
                                bold
                                editMode={editMode}
                                onChange={(e) => updateOffer(e.target.value, "offerRegistered")}
                                label="Date Registered"
                                value={offer?.offerRegistered && formatDate(offer?.offerRegistered)}
                            />
                        </PageGridItem>
                    </PageContentBodyGrid>
                    <PageGridDivider />
                    <PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid>
                        <PageGridItem container col="1 / span 6">
                            <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '1rem' }}>
                                <DetailSectionHeader header={portfolio ? "Managers" : "Funds"} icon={<Users />} />
                            </div>
                            <Table
                                minWidth={350}
                                tableStyle={{ maxWidth: 1675 }}
                                colWidths={portfolio ? managersColWidths : fundsColWidths}
                                dataMap={fundsDataMap(portfolio ? managers : offer?.funds)}
                                id='scheme-and-funds-expandable-table'
                            />
                        </PageGridItem>
                    </PageContentBodyGrid>
                </PageGridItem> */}

                {/* <PageGridItem container col="7 / span 6" gap='xl' style={{ display: 'flex', flexDirection: 'column', marginTop: '1.5rem' }}>
                    <PageContentBodyGrid rowGap="xl" gridColCount="6" subGrid divider='left'>
                        <PageGridItem container col="1 / span 6" direction='row' justifyContent='between'>
                            <DetailSectionHeader header="Footnotes" icon={<Clipboard />} />
                            <Button icon={<Plus />} onClick={createNote} disabled={!editMode}>add footnote</Button>
                        </PageGridItem>
                        <PageGridItem container col="1 / span 6">
                            {disclosureList(notes)}
                        </PageGridItem>
                    </PageContentBodyGrid>
                </PageGridItem> */}
            </PageContentBodyGrid >
        </Page >
    );
}

export default OfferPage;